import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MapPin, Loader2 } from "lucide-react";
import { Input } from "../ui/input";
import { inputCls } from "./FormControls";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AddressAutocomplete = ({ value, onChange, placeholder, testid }) => {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);
  const skipNext = useRef(false);

  useEffect(() => {
    if (skipNext.current) { skipNext.current = false; return; }
    if (value.trim().length < 3) { setResults([]); return; }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/address-search`, { params: { q: value }, signal: controller.signal });
        setResults(res.data.results || []);
        setOpen(true);
        setActive(-1);
      } catch (err) {
        if (!axios.isCancel(err)) setResults([]);
      } finally { setLoading(false); }
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [value]);

  const select = (label) => {
    skipNext.current = true;
    onChange(label);
    setResults([]);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); select(results[active].label); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative">
      <Input data-testid={testid} className={`${inputCls} pr-9`} value={value} onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown} onFocus={() => results.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder} autoComplete="off" aria-autocomplete="list" aria-expanded={open} />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
      </span>
      {open && results.length > 0 && (
        <ul data-testid={`${testid}-suggestions`} role="listbox"
          className="absolute z-20 mt-1 w-full bg-[#0B1035] border border-white/15 shadow-xl shadow-black/40 max-h-64 overflow-y-auto">
          {results.map((r, idx) => (
            <li key={r.label} role="option" aria-selected={idx === active}>
              <button type="button" data-testid={`${testid}-suggestion-${idx}`} onMouseDown={(e) => e.preventDefault()} onClick={() => select(r.label)}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${idx === active ? "bg-cyan-400/15 text-white" : "text-slate-200 hover:bg-white/5"}`}>
                <span className="block">{r.label}</span>
                {r.detail && <span className="block text-xs text-slate-500">{r.detail}</span>}
              </button>
            </li>
          ))}
          <li className="px-4 py-1.5 text-[10px] text-slate-600 border-t border-white/10">Suggestions © OpenStreetMap contributors</li>
        </ul>
      )}
    </div>
  );
};
